/**
 * AddEditPopup component is used to add or edit a variation analysis.
 * It handles the form data.
 * Depending on the edit mode, it will display the form with the data to modify or the form to add a new analysis.
 */

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "devextreme-react";
import {
  Form,
  SimpleItem,
  GroupItem,
  RequiredRule,
  Label,
} from "devextreme-react/form";
import PopupWrapper from "../../../../../components/common/pyramid/PopupWrapper";
import {
  OSIRIS_COUPON_METHOD,
  OSIRIS_INTERPOLATION_PD,
} from "data/ENUM/add-options";
import ConfirmPopup from "../../../../../components/common/pyramid/ConfirmPopup";
import OptionPopupOsiris from "../../../../common/pyramid/OptionPopupOsiris";
import { useAuth } from "providers/AuthProvider";
import { useGroupedItems } from "hooks/useGroupedItems";
import { useFilterByCurrentGroup } from "hooks/useFilterByCurrentGroup";
import useItemOwner from "hooks/useItemOwner";

export const popupConfigs = {
  add: {
    width: "auto",
    height: "auto",
    title: "New Variation Analysis",
    icon: "add",
  },
  rowEdit: {
    width: "auto",
    height: "auto",
    title: "Edit",
    icon: "edit",
  },
};

/**
 * Props:
 * - isVisible: boolean to display the popup.
 * - onConfirm: function to confirm the changes.
 * - onCancel: function to cancel the changes.
 * - datasetList: list of datasets.
 * - inputFormData: data to modify.
 * - editMode: boolean to modify or add a new analysis.
 */
const AddEditPopup = ({
  isVisible,
  onConfirm,
  onCancel,
  datasetList,
  inputFormData,
  editMode,
}) => {
  // Determine ownership of the item if edit mode is true
  const ownedItem = useItemOwner(editMode ? inputFormData : null, "group");
  const { userList, companyList } = useAuth();

  // Filter datasets on the current group
  const datasetsFilteredByGroup = useFilterByCurrentGroup(datasetList);
  const datasets = editMode ? datasetList : datasetsFilteredByGroup;

  // Group datasets by user
  const groupedDatasets = useGroupedItems(datasets, userList, companyList);

  /**
   * Gets the initial data for the form.
   * If the edit mode is true, returns inputFormData.
   * Otherwise, returns the initial data for the form.
   * @returns {Object} The initial data for the form.
   */
  const getInitialData = () => {
    if (editMode) {
      return inputFormData;
    } else {
      return {
        description: "",
        createdAt: null,
        inputDeparture: "",
        inputArrival: "",
        couponMethod: null,
        interpolationType: null,
        datasetDepartureId: null,
        datasetArrivalId: null,
        departureDatasetEvaluationDate: null,
        arrivalDatasetEvaluationDate: null,
      };
    }
  };

  /* State variables */
  const [popupStates, setPopupStates] = useState({
    isConfirmVisible: false,
    isDepartureOptionVisible: false,
    isArrivalOptionVisible: false,
  });
  const [localData, setLocalData] = useState(getInitialData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef(null);

  // Save the current config
  const currentConfig = useMemo(
    () => (editMode ? popupConfigs.rowEdit : popupConfigs.add),
    [editMode]
  );

  useEffect(() => {
    setLocalData({ ...inputFormData });
  }, [inputFormData]);

  useEffect(() => {
    if (localData.datasetDepartureId) {
      const selectedDataset = datasetList.find(
        (dataset) => dataset.id === localData.datasetDepartureId
      );
      setLocalData((prev) => ({
        ...prev,
        departureDatasetEvaluationDate: selectedDataset.evaluationDate,
      }));
    }
  }, [localData.datasetDepartureId, datasetList]);

  useEffect(() => {
    if (localData.datasetArrivalId) {
      const selectedDataset = datasetList.find(
        (dataset) => dataset.id === localData.datasetArrivalId
      );
      setLocalData((prev) => ({
        ...prev,
        arrivalDatasetEvaluationDate: selectedDataset.evaluationDate,
      }));
    }
  }, [localData.datasetArrivalId, datasetList]);

  /**
   * Checks if options are selected for a specific dataset type
   * @param {string} type - "Departure" or "Arrival"
   * @returns {boolean} - true if options are selected
   */
  const hasOptions = (type) => {
    return (
      localData[`${type}Bucket`] === true ||
      localData[`${type}ActuarialRate`] === true
    );
  };

  /**
   * Gets the message for the confirm popup based on which datasets are missing options
   * @returns {string} - The message to display
   */
  const getConfirmMessage = () => {
    const missingDeparture = !hasOptions("Departure");
    const missingArrival = !hasOptions("Arrival");

    if (missingDeparture && missingArrival) {
      return "No options have been selected for the two datasets (Departure and Arrival). Do you want to continue ?";
    } else if (missingDeparture) {
      return "No options have been selected for the dataset Departure. Do you want to continue ?";
    } else if (missingArrival) {
      return "No options have been selected for the dataset Arrival. Do you want to continue ?";
    }
    return "";
  };

  /**
   * Handles the cancel button click.
   */
  const handleCancel = () => {
    if (isSubmitting) return;

    if (editMode) {
      setLocalData({ ...inputFormData });
      formRef.current.instance().clear();
    } else {
      setLocalData(getInitialData());
      formRef.current.instance().clear();
    }
    onCancel();
  };

  /**
   * Handles the confirm button click. Checks the form data and confirms the changes.
   */
  const handleConfirm = () => {
    if (isSubmitting) return;

    const formInstance = formRef.current.instance();
    const editableFields = [];

    if (editMode) {
      editableFields.push("description");
    } else {
      editableFields.push(
        "description",
        "couponMethod",
        "interpolationType",
        "datasetDepartureId",
        "datasetArrivalId"
      );
    }

    const validationResult = formInstance.validate(editableFields);

    if (validationResult.isValid) {
      if (!editMode && (!hasOptions("Departure") || !hasOptions("Arrival"))) {
        setPopupStates((prev) => ({ ...prev, isConfirmVisible: true }));
      } else {
        confirmAndReset(localData);
      }
    } else {
      console.log("Invalid fields:", validationResult.brokenRules);
    }
  };

  const confirmAndReset = async (data) => {
    setIsSubmitting(true);
    try {
      await onConfirm(data);
      formRef.current.instance().resetValues();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmYes = () => {
    if (isSubmitting) return;
    confirmAndReset(localData);
    setPopupStates((prev) => ({ ...prev, isConfirmVisible: false }));
  };

  const handleFieldChange = (e) => {
    setLocalData({ ...localData, [e.dataField]: e.value });
  };

  /**
   * Handles the confirmation of options for departure or arrival
   * @param {Object} updatedData - The updated data
   * @param {String} type - "Departure" or "Arrival"
   */
  const handleOptionsConfirm = (updatedData, type) => {
    setLocalData((prevData) => ({
      ...prevData,
      ...updatedData,
    }));
    setPopupStates((prev) => ({
      ...prev,
      [`is${type}OptionVisible`]: false,
    }));
  };

  /**
   * Handles the cancellation of options for departure or arrival
   * @param {Object} result - The confirmation results
   * @param {String} type - "Departure" or "Arrival"
   */
  const handleOptionsCancel = (result, type) => {
    setPopupStates((prev) => ({
      ...prev,
      [`is${type}OptionVisible`]: false,
    }));

    if (result && result.keepData === false) {
      setLocalData((prevData) => {
        const fieldsToRemove = [
          `${type}Bucket`,
          `${type}bucketRules`,
          `${type}ActuarialRate`,
          `${type}actuarialRateRules`,
        ];

        const newData = { ...prevData };
        fieldsToRemove.forEach((field) => delete newData[field]);

        return newData;
      });
    }
  };

  // Adding prefix and redirect to the appropriate function
  const handleDepartureOptionsConfirm = (updatedData) =>
    handleOptionsConfirm(updatedData, "Departure");
  const handleDepartureOptionsCancel = (result) =>
    handleOptionsCancel(result, "Departure");
  const handleArrivalOptionsConfirm = (updatedData) =>
    handleOptionsConfirm(updatedData, "Arrival");
  const handleArrivalOptionsCancel = (result) =>
    handleOptionsCancel(result, "Arrival");

  return (
    <>
      <PopupWrapper
        {...currentConfig}
        isVisible={isVisible}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        isConfirmDisabled={isSubmitting || (editMode && !ownedItem)}
        isCancelDisabled={isSubmitting}
        showLoading={isSubmitting}
      >
        <Form
          formData={localData}
          ref={formRef}
          onFieldDataChanged={handleFieldChange}
        >
          <GroupItem caption="Informations">
            <SimpleItem
              dataField="description"
              editorType="dxTextBox"
              editorOptions={{
                readOnly: editMode && !ownedItem,
                placeholder: "Enter description",
                onValueChanged: (e) =>
                  setLocalData({ ...localData, description: e.value }),
              }}
              isRequired={true}
            >
              <Label text="Description" />
              <RequiredRule />
            </SimpleItem>
          </GroupItem>

          <GroupItem caption="Data" colCount={2}>
            <SimpleItem
              dataField="couponMethod"
              editorType="dxSelectBox"
              editorOptions={{
                items: OSIRIS_COUPON_METHOD,
                valueExpr: "id",
                displayExpr: "label",
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="ECL Coupon method" />
            </SimpleItem>

            <SimpleItem
              dataField="interpolationType"
              editorType="dxSelectBox"
              editorOptions={{
                items: OSIRIS_INTERPOLATION_PD,
                valueExpr: "id",
                displayExpr: "label",
                readOnly: editMode,
                required: !editMode,
              }}
              isRequired={!editMode}
            >
              <Label text="Interpolation PD/LGD" />
            </SimpleItem>
          </GroupItem>

          <GroupItem colCount={2}>
            <GroupItem caption="Departure">
              <SimpleItem
                dataField="datasetDepartureId"
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: groupedDatasets,
                  valueExpr: "id",
                  displayExpr: "description",
                  grouped: true,
                  readOnly: editMode,
                  required: !editMode,
                }}
                isRequired={!editMode}
              >
                <Label text="First" />
              </SimpleItem>

              <SimpleItem
                dataField="departureDatasetEvaluationDate"
                editorType="dxDateBox"
                editorOptions={{
                  readOnly: true,
                  displayFormat: "yyyy-MM-dd",
                  value: localData.departureDatasetEvaluationDate,
                }}
              >
                <Label text="Evaluation Date" />
              </SimpleItem>
              <SimpleItem>
                <Label text="Options Departure" />
                <Button
                  text="Edit"
                  icon="edit"
                  width="auto"
                  type="default"
                  stylingMode="outlined"
                  onClick={() =>
                    setPopupStates((prev) => ({
                      ...prev,
                      isDepartureOptionVisible: true,
                    }))
                  }
                />
              </SimpleItem>
            </GroupItem>

            <GroupItem caption="Arrival">
              <SimpleItem
                dataField="datasetArrivalId"
                editorType="dxSelectBox"
                editorOptions={{
                  dataSource: groupedDatasets,
                  valueExpr: "id",
                  displayExpr: "description",
                  grouped: true,
                  readOnly: editMode,
                  required: !editMode,
                }}
                isRequired={!editMode}
              >
                <Label text="Second" />
              </SimpleItem>

              <SimpleItem
                dataField="arrivalDatasetEvaluationDate"
                editorType="dxDateBox"
                editorOptions={{
                  readOnly: true,
                  displayFormat: "yyyy-MM-dd",
                  value: localData.arrivalDatasetEvaluationDate,
                }}
              >
                <Label text="Evaluation Date" />
              </SimpleItem>
              <SimpleItem>
                <Label text="Options Arrival" />
                <Button
                  text="Edit"
                  icon="edit"
                  width="auto"
                  type="default"
                  stylingMode="outlined"
                  onClick={() =>
                    setPopupStates((prev) => ({
                      ...prev,
                      isArrivalOptionVisible: true,
                    }))
                  }
                />
              </SimpleItem>
            </GroupItem>
          </GroupItem>
        </Form>
      </PopupWrapper>

      <ConfirmPopup
        isVisible={popupStates.isConfirmVisible}
        onNo={() =>
          setPopupStates((prev) => ({ ...prev, isConfirmVisible: false }))
        }
        onYes={handleConfirmYes}
        message={getConfirmMessage()}
      />

      <OptionPopupOsiris
        isVisible={popupStates.isDepartureOptionVisible}
        onConfirm={handleDepartureOptionsConfirm}
        onCancel={(result) => handleDepartureOptionsCancel(result)}
        title="Departure"
        data={localData}
        prefix="Departure"
      />

      <OptionPopupOsiris
        isVisible={popupStates.isArrivalOptionVisible}
        onConfirm={handleArrivalOptionsConfirm}
        onCancel={(result) => handleArrivalOptionsCancel(result)}
        title="Arrival"
        data={localData}
        prefix="Arrival"
      />
    </>
  );
};

export default AddEditPopup;
